import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import axios, { CancelToken } from "axios";
import { useTranslation } from "react-i18next";

import { StatusCode } from "api/protocols";
import { GetBankAccount } from "api/rpc/2024-04/facilityAdmin/facility/externalAcount";
import { PutAcceptTerms } from "api/rpc/2024-04/facilityAdmin/payment/teeonPayments/accounts";

import { showError, showSuccess } from "redux/actions/ui";
import { TBankAccountExternal } from "redux/reducers/models/facility";
import { valueToString } from "helpers/Helpers";
import { useAppDispatch, useAppSelector } from "hooks/redux";

import Page from "components/page/Page";
import { Badge } from "components/badge/Badge";
import DataTable from "../../../../../components/dataTable/DataTable";

import "../facility/facility.scss";

export default function BankAccounts() {
  const history = useHistory();
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const authStore = useAppSelector(store => store.authStore);
  const permissions = authStore?.user?.permissions;

  const [bankAccounts, setBankAccounts] = useState<TBankAccountExternal[]>(undefined);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadBankAccounts(source.token);
    return () => source.cancel();
  }, []);

  async function loadBankAccounts(token?: CancelToken) {
    if (bankAccounts !== undefined) {
      setBankAccounts(undefined);
    }
    const res = await GetBankAccount(undefined, token ? false : true, token);

    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      setBankAccounts([]);
      dispatch;
      return;
    }

    setBankAccounts(res.data);
  }

  async function handleAcceptTerms() {
    const res = await PutAcceptTerms(true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error accepting terms")));
      return;
    }
    dispatch(showSuccess(t("Terms accepted successfully")));
  }

  const primaryAction = {
    content: t("New Bank Account"),
    action: () => history.push("/admin/settings/bank_accounts/new"),
  };

  const secondaryAction = {
    content: t("Accept Terms"),
    action: handleAcceptTerms,
  };

  return (
    <Page
      title={t("Bank Accounts")}
      subtitle={t("Manage bank accounts that are set up to receive deposits")}
      primaryAction={permissions.bank_accounts_create ? primaryAction : null}
      secondaryActions={[secondaryAction]}
      narrow
    >
      <DataTable
        columns={[
          { label: t("Number") },
          { label: t("Account Holder") },
          { label: t("Country") },
          { label: t("Currency") },
          { label: t("Bank Name") },
          { label: t("Type") },
        ]}
        loading={bankAccounts === undefined}
      >
        {bankAccounts?.map(account => (
          <tr
            key={account.id}
            className="clickable"
            onClick={() => history.push("/admin/settings/bank_accounts/" + String(account.id))}
          >
            <td className="table-cell-text-lead">{account.last_4_redacted}</td>
            <td>{account.account_holder_name}</td>
            <td>{valueToString(account.country, "uppercase")}</td>
            <td>
              {valueToString(account.currency, "uppercase")}
              {account.currency_default ? (
                <Badge type="gray" size="medium">
                  {t("Default")}
                </Badge>
              ) : null}
            </td>
            <td>{account.bank_name}</td>
            <td>{valueToString(account.type, "uppercase")}</td>
          </tr>
        ))}
      </DataTable>
    </Page>
  );
}
